import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border: 1.5px solid ${({ $isOpen, $hasSelection }) =>
    $isOpen ? "#3f88a5" : $hasSelection ? "#3f88a5" : "#e2e8f0"};
  border-radius: 8px;
  background: ${({ $hasSelection }) => ($hasSelection ? "#f0f7ff" : "#fff")};
  cursor: pointer;
  min-height: 34px;
  font-size: 13px;
  color: ${({ $hasSelection }) => ($hasSelection ? "#1e293b" : "#94a3b8")};
  transition: all 0.2s;

  &:hover {
    border-color: #3f88a5;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.div`
  margin-left: 8px;
  font-size: 10px;
  color: #64748b;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s;
`;

/* Rendered through a portal into document.body (see below), positioned via
   inline style computed from the header's bounding rect. This lets the panel
   escape any ancestor's overflow:hidden/auto clipping (e.g. a scrollable
   modal body) instead of being cropped by it. */
const DropdownBody = styled.div`
  position: fixed;
  z-index: 10050;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  display: flex;
  flex-direction: column;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  outline: none;
  font-size: 13px;
  background: #f8fafc;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  &::placeholder {
    color: #94a3b8;
  }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  padding: 4px 0;
  flex: 1;
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #1e293b;
  background: ${({ $isSelected }) => ($isSelected ? "#f0f7ff" : "transparent")};

  &:hover {
    background: #f1f5f9;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
  accent-color: #3f88a5;
  cursor: pointer;
`;

const MultiSelectDropdown = ({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  showSelectAll = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [coords, setCoords] = useState(null);
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);

  // Handle outside click — the panel is portaled to document.body, so it's
  // no longer a DOM descendant of containerRef; it must be checked separately.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        (!bodyRef.current || !bodyRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on scroll/resize of anything outside the panel itself (scrolling
  // the options list or the search box shouldn't close it), so the panel
  // never drifts away from its trigger.
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event) => {
      if (bodyRef.current && bodyRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    const handleResize = () => setIsOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selected.includes(opt.value));

  const handleToggle = () => {
    if (!isOpen && headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsOpen((prev) => !prev);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set([...selected, ...filteredOptions.map((o) => o.value)]);
      onChange(Array.from(newSelected));
    } else {
      const filteredValues = new Set(filteredOptions.map((o) => o.value));
      const newSelected = selected.filter((val) => !filteredValues.has(val));
      onChange(newSelected);
    }
  };

  const handleSelectOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const getHeaderText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === options.length && options.length > 1) return "All selected";
    
    // Find labels of selected options
    const selectedLabels = options
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label);

    if (selectedLabels.length > 0) {
      return selectedLabels.join(", ");
    }
    return placeholder;
  };

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownHeader
        ref={headerRef}
        onClick={handleToggle}
        $isOpen={isOpen}
        $hasSelection={selected.length > 0}
      >
        <HeaderText>{getHeaderText()}</HeaderText>
        <Chevron $isOpen={isOpen}>▼</Chevron>
      </DropdownHeader>

      {isOpen && coords && ReactDOM.createPortal(
        <DropdownBody ref={bodyRef} style={{ top: coords.top, left: coords.left, width: coords.width }}>
          <SearchInput
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <OptionsList>
            {filteredOptions.length > 0 ? (
              <>
                {showSelectAll && (
                  <OptionItem $isSelected={isAllSelected}>
                    <Checkbox
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                    <span style={{ fontWeight: 600 }}>Select All</span>
                  </OptionItem>
                )}
                {filteredOptions.map((opt) => (
                  <OptionItem
                    key={opt.value}
                    $isSelected={selected.includes(opt.value)}
                  >
                    <Checkbox
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleSelectOption(opt.value)}
                    />
                    {opt.label}
                  </OptionItem>
                ))}
              </>
            ) : (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                No options found
              </div>
            )}
          </OptionsList>
        </DropdownBody>,
        document.body
      )}
    </DropdownContainer>
  );
};

export default MultiSelectDropdown;
